import { HStack } from '@chakra-ui/react'
import { useCallback, useMemo, useState } from 'react'

import { Button } from '@app/components/ui/button'
import { toaster } from '@app/components/ui/toaster'
import { Tooltip } from '@app/components/ui/tooltip'
import useGetObject from '@app/hooks/useGetObject'
import useGetObjectsByProperties from '@app/hooks/useGetObjectsByProperties'
import useStoreCurrentUser from '@app/hooks/useStoreCurrentUser'
import Can from '@app/shared/authorization/can'
import { useMapRunStatsMutation } from '@graphql/queries'

const RunStats = ({ strategyId }) => {
  const { formatDateTimeInUserTimeZone } = useStoreCurrentUser()
  const strategy = useGetObject(strategyId, 'strategy')
  const [loading, setLoading] = useState(false)
  const [, run] = useMapRunStatsMutation()
  const nodes = useGetObjectsByProperties('node', {
    strategyId
  })

  const edges = useGetObjectsByProperties('edge', {
    strategyId
  })

  const onClick = useCallback(
    async (e) => {
      e.stopPropagation()
      setLoading(true)
      try {
        await run({ input: { strategyId } })
      } catch (err) {
        toaster.error({ title: 'Error generating baselines', description: err.message, duration: 8000 })
      } finally {
        setLoading(false)
      }
    },
    [run, strategyId]
  )

  const timestamp = useMemo(() => {
    const stamps = []
    if (strategy?.statsUpdatedAt) {
      stamps.push(strategy.statsUpdatedAt)
    }

    for (const node of nodes) {
      if (node.statsUpdatedAt) {
        stamps.push(node.statsUpdatedAt)
      }
    }

    for (const edge of edges) {
      if (edge.correlationScoreUpdatedAt) {
        stamps.push(edge.correlationScoreUpdatedAt)
      }
    }

    // note that we show the oldest timestamp
    return stamps.sort()[0] || null
  }, [nodes, edges, strategy?.statsUpdatedAt])

  const updatedAt = timestamp ? formatDateTimeInUserTimeZone(timestamp, 'MMMM do, yyy h:mm aaa') : 'never'

  return (
    <HStack>
      <Can I="update" a="strategy">
        <Tooltip content={`Last updated: ${updatedAt}`}>
          <Button size="xs" py={0} px={1} h="inherit" loading={loading} onClick={onClick} variant="outline">
            Update stats
          </Button>
        </Tooltip>
      </Can>
    </HStack>
  )
}

export default RunStats
